import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["status", "loginButton", "friendPanel", "sendButton", "message", "copyResult"]
  static values = {
    liffId: String,
    photoUrl: String,
    message: String,
    oaMessageUrl: String,
    addFriendUrl: String
  }

  connect() {
    this.sent = false
    this.liffReady = false
    this.initializeLiff()
  }

  async initializeLiff() {
    if (!this.liffIdValue) {
      this.showFallback("LIFFの設定がまだ完了していないため、写真提出ページへ進めます。")
      return
    }

    if (!window.liff) {
      this.showFallback("LINE機能を読み込めませんでした。下の案内文をコピーしてお使いください。")
      return
    }

    try {
      await window.liff.init({ liffId: this.liffIdValue })
      this.liffReady = true

      if (!window.liff.isLoggedIn()) {
        this.statusTarget.textContent = "LINEにログインして続けてください。"
        this.loginButtonTarget.hidden = false
        return
      }

      await this.refreshFriendship()
    } catch (_error) {
      this.showFallback("LINEの状態を確認できませんでした。写真提出ページへ進むか、案内文をコピーしてください。")
    }
  }

  login() {
    if (!this.liffReady || !window.liff?.login) return

    window.liff.login({ redirectUri: window.location.href })
  }

  async refreshFriendship() {
    if (!this.liffReady || !window.liff?.getFriendship) {
      this.showFallback("友だち状態を確認できませんでした。公式LINEのトークを開くか、案内文をコピーしてください。")
      return
    }

    try {
      const friendship = await window.liff.getFriendship()
      if (friendship.friendFlag) {
        this.friendPanelTarget.hidden = true
        if (this.canSendMessagesToCurrentChat()) {
          this.sendButtonTarget.hidden = false
          this.sendButtonTarget.disabled = false
          this.statusTarget.textContent = "友だち追加済みです。このトークに案内を送れます。"
        } else {
          this.sendButtonTarget.hidden = true
          this.sendButtonTarget.disabled = true
          this.statusTarget.textContent = this.oaMessageUrlValue
            ? "友だち追加済みです。公式LINEのトークを開いて案内を送ってください。"
            : "友だち追加済みです。下の案内文をコピーして送ってください。"
        }
      } else {
        this.friendPanelTarget.hidden = false
        this.sendButtonTarget.hidden = true
        this.sendButtonTarget.disabled = true
        this.statusTarget.textContent = "友だち追加後に、案内をLINEへ送れます。"
      }
    } catch (_error) {
      this.friendPanelTarget.hidden = !this.addFriendUrlValue
      this.sendButtonTarget.hidden = true
      this.sendButtonTarget.disabled = true
      this.statusTarget.textContent = "友だち状態を確認できませんでした。公式LINEを追加してから再確認してください。"
    }
  }

  async sendMessage() {
    if (this.sent || this.sendButtonTarget.disabled) return

    if (!window.liff?.isApiAvailable?.("sendMessages")) {
      this.showFallback("この端末ではLINEへの直接送信に対応していません。案内文をコピーしてください。")
      return
    }

    this.sendButtonTarget.disabled = true

    try {
      await window.liff.sendMessages([{ type: "text", text: this.messageValue }])
      this.sent = true
      this.statusTarget.textContent = "案内をLINEへ送りました。写真提出ページへ進めます。"
    } catch (_error) {
      this.sendButtonTarget.disabled = false
      this.showFallback("LINEへ送信できませんでした。案内文をコピーして送ってください。")
    }
  }

  canSendMessagesToCurrentChat() {
    if (!window.liff?.isInClient?.()) return false
    if (!window.liff?.isApiAvailable?.("sendMessages")) return false

    const contextType = window.liff.getContext?.()?.type
    return ["utou", "group", "room"].includes(contextType)
  }

  async copyMessage() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(this.messageValue)
      } else {
        this.messageTarget.select()
        document.execCommand("copy")
      }
      this.copyResultTarget.textContent = "コピーしました。"
    } catch (_error) {
      this.messageTarget.focus()
      this.messageTarget.select()
      this.copyResultTarget.textContent = "文面を選択しました。コピーしてください。"
    }
  }

  showFallback(message) {
    this.statusTarget.textContent = message
    this.sendButtonTarget.hidden = true
    this.sendButtonTarget.disabled = true
  }
}
